import { t } from 'i18next'
import { useDispatch, useSelector, useStore, shallowEqual } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { algoliaQueryService } from 'redux/services/clientsServices'
import {
  addWarrantyToCart,
  updateCartItem,
  removeFromCart,
  getPayloadProduct,
  buildWarrantyPayload,
} from 'redux/actions/cartActions'
import { noWarranty, WARRANTY_PRODUCT_SKU } from 'Utils/constants'

const useHandleWarranty = (options = { updateIfExists: false }) => {
  const dispatch = useDispatch()
  const store = useStore()
  const queryClient = useQueryClient()
  const company = useSelector(
    state => ({
      WarehouseId: state.company.WarehouseId,
      WarehouseCode: state.company.WarehouseCode,
      WarehouseDescription: state.company.WarehouseDescription,
    }),
    shallowEqual
  )
  const warehouseAddress = useSelector(
    state => state.cotizacion.QuoteHeader.WarehouseAddress
  )
  const handleRemoveWarrantyIfNeeded = (selectedWarranty, productId) => {
    const isNoWarranty =
      !selectedWarranty || selectedWarranty.Code === noWarranty.Code

    if (!options.updateIfExists || !isNoWarranty || !productId) return false

    const existingWarranty = store
      .getState()
      .cart.cart.find(
        item => item.IsExtendedWarranty && item.WarrantyProductId === productId
      )

    if (existingWarranty) {
      dispatch(removeFromCart({ product: existingWarranty }))
      dispatch(
        updateCartItem({
          updatedFields: {
            WarrantyYears: null,
            WarrantyProductId: null,
          },
          matchCriteria: { ProductId: productId, IsExtendedWarranty: false },
        })
      )
    }

    return true
  }

  const fetchWarrantyProduct = async () => {
    const data = await queryClient.fetchQuery({
      queryKey: ['algolia-warranty'],
      queryFn: () => algoliaQueryService({ query: 'Y071-000002' }),
      staleTime: 1000 * 60 * 60 * 6,
      cacheTime: 1000 * 60 * 60 * 6,
    })

    return data?.data?.hits?.[0] || null
  }

  const buildEnrichedWarrantyProduct = (
    warrantyProduct,
    selectedWarranty,
    existingLineNum
  ) => {
    const { WarehouseId, WarehouseCode, WarehouseDescription } = company

    const warehouse = {
      WarehouseId,
      WarehouseCode,
      WarehouseDescription,
      WarehouseAddress: warehouseAddress,
    }

    const payloadBase = buildWarrantyPayload({
      warrantyProduct,
      selectedWarranty,
    })

    const enriched = getPayloadProduct(
      payloadBase,
      { product: { ...warrantyProduct, BasePrice: payloadBase.Price } },
      warehouse,
      1,
      selectedWarranty.CalculatedPrice
    )

    return {
      ...enriched,
      LineNum: existingLineNum,
      IsExtendedWarranty: true,
      WarrantyYears: selectedWarranty.WarrantyYears,
      WarrantyProductId: selectedWarranty.WarrantyProductId,
      WarrantyPercent: selectedWarranty.WarrantyPercent,
    }
  }

  const handleWarrantyDispatch = (
    warrantyProduct,
    selectedWarranty,
    productId,
    quantity
  ) => {
    const existingWarranty = productId
      ? store
          .getState()
          .cart.cart.find(
            item =>
              item.IsExtendedWarranty && item.WarrantyProductId === productId
          )
      : undefined

    if (options.updateIfExists && existingWarranty) {
      const enrichedProduct = buildEnrichedWarrantyProduct(
        warrantyProduct,
        selectedWarranty,
        existingWarranty.LineNum
      )

      dispatch(
        updateCartItem({
          updatedFields: {
            ...enrichedProduct,
            // Preserve existing warranty quantity when changing warranty type
            Quantity: existingWarranty.Quantity,
          },
          matchCriteria: {
            WarrantyProductId: productId,
            IsExtendedWarranty: true,
          },
        })
      )
    } else {
      dispatch(
        addWarrantyToCart(
          selectedWarranty,
          warrantyProduct,
          productId,
          quantity
        )
      )
    }
  }

  const addWarranty = async ({ selectedWarranty, productId, quantity }) => {
    try {
      const removed = handleRemoveWarrantyIfNeeded(selectedWarranty, productId)
      if (removed) return

      const warrantyProduct = await fetchWarrantyProduct()
      if (!warrantyProduct) return

      handleWarrantyDispatch(
        warrantyProduct,
        selectedWarranty,
        productId,
        quantity
      )
    } catch (error) {
      console.error('Error adding warranty to cart:', error)
    }
  }

  const isWarrantyItem = item =>
    item.IsExtendedWarranty || item.WarrantyProductId != null

  const getDispatchQty = item =>
    typeof item.solicitud_despacho === 'string'
      ? Number(item.solicitud_despacho.replaceAll(',', ''))
      : Number(item.solicitud_despacho) || 0

  const validateWarrantyDispatch = dispatchQtyState => {
    const selectedItems = dispatchQtyState.filter(item => item.isSelected)

    if (selectedItems.length > 0 && selectedItems.every(isWarrantyItem)) {
      return {
        isValid: false,
        warningText: t('SELL_ORDER_T.WARNING_ONLY_WARRANTY_DISPATCH'),
      }
    }

    for (const item of selectedItems) {
      if (!isWarrantyItem(item)) continue

      const warrantyQty = getDispatchQty(item)
      const parentItem = dispatchQtyState.find(
        p =>
          p.productId === item.WarrantyProductId &&
          !isWarrantyItem(p) &&
          p.isSelected
      )
      const parentQty = parentItem ? getDispatchQty(parentItem) : 0

      if (warrantyQty > parentQty) {
        return {
          isValid: false,
          warningText: t('SELL_ORDER_T.WARNING_WARRANTY_QTY_EXCEEDS_PRODUCT'),
        }
      }
    }

    return { isValid: true, warningText: null }
  }

  const getWarrantyProductText = product => {
    if (!product) return ''

    const isWarranty =
      product.IsExtendedWarranty ||
      product.Sku === WARRANTY_PRODUCT_SKU ||
      product.SystemCode === WARRANTY_PRODUCT_SKU

    if (isWarranty && product.WarrantyYears) {
      const title = t('EXTENDED_WARRANTY_T.TITLE_CARD_PRODUCT', {
        warrantyYears: product.WarrantyYears,
      })
      const plural = product.WarrantyYears > 1 ? 's' : ''
      const reference = product.Reference || ''

      return `${title}${plural}\n${reference}`
    }

    return product.ProductName || ''
  }

  const hasWarrantyForProduct = product => {
    if (!product?.ProductId) return false

    return store
      .getState()
      .cart.cart.some(
        item =>
          item.IsExtendedWarranty &&
          item.WarrantyProductId === product.ProductId
      )
  }

  return {
    addWarranty,
    getWarrantyProductText,
    hasWarrantyForProduct,
    validateWarrantyDispatch,
  }
}

export default useHandleWarranty
